import Navigation from "@/components/Navigation";
import HeroBanner from "@/components/HeroBanner";
import PricingCTA from "@/components/PricingCTA";
import FeaturedBillboards from "@/components/FeaturedBillboards";
import FeaturedPackages from "@/components/FeaturedPackages";
import MillionViewInventories from "@/components/MillionViewInventories";
import ConvenienceUSP from "@/components/ConvenienceUSP";
import CoverageUSP from "@/components/CoverageUSP";
import ROIUSP from "@/components/ROIUSP";
import ClientsServed from "@/components/ClientsServed";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer1";
import { useEffect, useState } from "react";
import type { Configuration } from "@/types/config";
import { normalizeDefault } from "@/utils/validationUtils";
import SustainabilitySection from "@/components/SustainabilitySection";

interface LandingProps {
  yourLogo: string;
  footerLogo: string;
  currencyCode: string;
  countryId: string;
  countryName: string;
  base_path: string;
  exposure: string;
}

export default function Landing({ yourLogo, footerLogo, currencyCode, countryId, countryName, base_path, exposure }: LandingProps) {

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [isLandingPage, setIsLandingPage] = useState(true);
  const [configData, setConfigData] = useState<any[]>([]);
  const [contentData, setContentData] = useState<any[]>([]);
  const [isLoginEnabled, setIsLoginEnabled] = useState(true);
  const [isExploreEnabled, setIsExploreEnabled] = useState(true);

  // Fetch config and content (adapt as needed)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoadingConfig(true);
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()}`;
        const configResponse = await fetch(url);
        if (!configResponse.ok) throw new Error("Config fetch failed");
        const configData = await configResponse.json();
        setConfigData(configData);


        // Example: set landing page flag and logo
        const landingPageConfig = configData.find((page: any) => page.name === "Landing Page");

        console.log("Landing Page Config:", landingPageConfig);

        const landingPageEnabled = landingPageConfig?.configurations?.find((c: any) => c.key === "do_you_need_landing_page")?.default ?? true;
        setIsLandingPage(landingPageEnabled);

        const exploreConfig = landingPageConfig.configurations.find(
          (config: Configuration) => config.key === "Explore"
        );
        const loginConfig = landingPageConfig.configurations.find(
          (config: Configuration) => config.key === "login"
        );
        if (exploreConfig) {

          setIsExploreEnabled(normalizeDefault(exploreConfig.default));
        }
        if (loginConfig) {
          setIsLoginEnabled(normalizeDefault(loginConfig.default));
        }
        if (landingPageConfig) {
          const config = landingPageConfig.configurations.find(
            (config: Configuration) => config.key === "do_you_need_landing_page"
          );

          // Set landing page flag based on configuration
          // If config is undefined or not found, default to keeping the landing page visible
          const landingPageEnabled = config ? normalizeDefault(config.default) : true;
          setIsLandingPage(landingPageEnabled);

          const exploreConfig = landingPageConfig.configurations.find(
            (config: Configuration) => config.key === "Explore"
          );
          const loginConfig = landingPageConfig.configurations.find(
            (config: Configuration) => config.key === "login"
          );
          if (exploreConfig) {

            setIsExploreEnabled(normalizeDefault(exploreConfig.default));
          }
          if (loginConfig) {
            setIsLoginEnabled(normalizeDefault(loginConfig.default));
          }
        }
      } catch (error) {
        setIsLandingPage(true);
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchConfig();
  }, []);

  const isFieldEnabled = (key: string, config: any[]) => {
    const landingPageConfig = config?.find((page: { name: string }) => page.name === "Landing Page");
    const configdata = landingPageConfig?.configurations || [];
    const fieldConfig = configdata?.find((field: any) => field.key === key);
    return fieldConfig ? normalizeDefault(fieldConfig.default) : true;
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;

        const response = await fetch(url);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error("Non-JSON response:", text);
          throw new Error("Response is not JSON");
        }

        const data = await response.json();
        setContentData(data);

        const pageData = data.find((page: any) => page.name === "Landing Page");
        if (!pageData) {
          console.warn("Landing Page data not found");
          return;
        }
      } catch (error) {
        console.error("Error fetching content:", error);
      }
    };

    fetchContent();
  }, []);


  return (
    <div className="min-h-screen bg-warm-white">
      <Navigation
        configData={configData}
        yourLogo={yourLogo}
        base_path={base_path}
        mode="landing"
      />
      <HeroBanner
        isExploreEnabled={isExploreEnabled}
        isLoginEnabled={isLoginEnabled}
        contentData={contentData}
        configData={configData}
        base_path={base_path}
      />
      {isFieldEnabled('low_emission_high_impact', configData) && (
        <SustainabilitySection base_path={base_path}/>
      )}
      {isFieldEnabled('revolutionary pricing model', configData) && (
        <PricingCTA />
      )}
      {isFieldEnabled('featured packages', configData) && (
        <FeaturedPackages
          base_path={base_path}
          countryId={countryId}
          currencyCode={currencyCode}
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
        />
      )}
      {isFieldEnabled('featured properties', configData) && (
        <FeaturedBillboards
          base_path={base_path}
          countryId={countryId}
          currencyCode={currencyCode}
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
        />
      )}
      {isFieldEnabled('popular properties', configData) && (
        <MillionViewInventories

          countryId={countryId}
          base_path={base_path}
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          exposure={exposure}
        />
      )}
      {isFieldEnabled('speed&efficiency', configData) && (
        <ConvenienceUSP
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          base_path={base_path}
        />
      )}
      {isFieldEnabled('global coverage', configData) && (
        <CoverageUSP
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          base_path={base_path}
        />
      )}
      {isFieldEnabled('ROI optimization', configData) && (
        <ROIUSP
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          base_path={base_path}
        />
      )}
      {isFieldEnabled('clients we have served', configData) && (
        <ClientsServed />
      )}
      {isFieldEnabled('clients testimonials', configData) && (
        <Testimonials />
      )}
      {isFieldEnabled('faq', configData) && (
        <FAQ />
      )}
      {isFieldEnabled('contact form', configData) && (
        <ContactForm countryName={countryName}
          contentData={contentData}
          configData={configData}
        />
      )}
      <Footer configData={configData} yourLogo={footerLogo} base_path={base_path} />
    </div>
  );
}
